import { Divider, Typography } from '@mui/material';
import { InvestmentRevaluationOption } from 'further-types/lp/investment';
import BasicSelect from 'further-ui/components/BasicSelect';
import FormField from 'further-ui/components/FormField';
import { Column } from 'further-ui/components/Layout';
import { investment as rules } from 'further-ui/validations';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { HoldingClassInvestmentTotal } from 'routes/LpPages/Company/Revaluations/CreateRevaluation/types';
import { InvestmentHistory } from 'routes/LpPages/Company/Revaluations/types';
import {
  applyRevaluationOption,
  RevaluationBaseline,
} from './deriveRevaluations';

const revaluationText =
  'This share price is different from other shares you hold in this company. To avoid creating two valuations for the same share class and needing to revalue it later, you can process an automatic revaluation below.';

const additionalRevaluationText =
  'As this investment has a different price for an existing share class, this share class will have two valuations in the system, so will require revaluation later if you do not revalue it here.';

type RevaluationOptionsProps = {
  holdingClassInvestmentTotals: Array<HoldingClassInvestmentTotal>;
  baseline: RevaluationBaseline;
  investmentHistory: InvestmentHistory;
};

const RevaluationOptions: React.FC<RevaluationOptionsProps> = ({
  holdingClassInvestmentTotals,
  baseline,
  investmentHistory,
}) => {
  const { control, watch, setValue } =
    useFormContext<rules.CreateInvestmentFormValues>();
  const holdingClasses = watch('holdingClasses');

  const { replace: replaceNewRevaluations } = useFieldArray({
    control,
    name: 'newRevaluations',
  });

  const handleRevaluationOptionChange = (
    option: InvestmentRevaluationOption,
  ) => {
    setValue('revaluationOption', option);

    const applied = applyRevaluationOption(
      option,
      baseline,
      holdingClasses,
      investmentHistory,
    );
    replaceNewRevaluations(applied.newRevaluations);
    setValue('currentUnitPrice', applied.currentUnitPrice);
    setValue('currentHoldValue', applied.currentHoldValue);
  };

  const investmentHasOneHoldingClass = holdingClasses.length === 1;

  const holdingClassHasInvestments = holdingClassInvestmentTotals.some((hc) =>
    holdingClasses
      .map((holdingClass) => holdingClass.holdingClassId)
      .includes(hc.holdingClassId),
  );

  const unitPriceDifferentForHoldingClasses =
    !holdingClassInvestmentTotals.length
      ? false
      : holdingClasses.some((holdingClass) => {
          const existingHoldingClass = holdingClassInvestmentTotals.find(
            (hc) => hc.holdingClassId === holdingClass.holdingClassId,
          );
          return (
            existingHoldingClass &&
            existingHoldingClass.latestUnitPrice !== holdingClass.unitPriceTcy
          );
        });

  const options = [
    ...(investmentHasOneHoldingClass
      ? [
          {
            label: 'Revalue all existing shares with this share price and date',
            value: InvestmentRevaluationOption.AllShares,
          },
        ]
      : []),
    ...(holdingClassHasInvestments
      ? [
          {
            label:
              'Revalue this share class only with this share price and date ',
            value: InvestmentRevaluationOption.ShareClassOnly,
          },
        ]
      : []),
    {
      label: 'Do not revalue',
      value: InvestmentRevaluationOption.DoNotRevalue,
    },
    {
      label: 'Manually input share price and date',
      value: InvestmentRevaluationOption.ManuallyInput,
    },
  ];

  return (
    <Column spacing="lg">
      <Column spacing="xs">
        <Typography variant="h5">Revalue existing shares</Typography>
        <Typography variant="body2">
          {revaluationText}{' '}
          {!!unitPriceDifferentForHoldingClasses && additionalRevaluationText}
        </Typography>
      </Column>
      <Divider />
      <FormField label="Choose revaluation option*" size="lg">
        <Controller
          control={control}
          name="revaluationOption"
          render={({ field, fieldState }) => (
            <BasicSelect
              fullWidth
              value={field.value}
              onChange={handleRevaluationOptionChange}
              options={options}
              error={fieldState.invalid}
              helperText={fieldState.error?.message}
            />
          )}
        />
      </FormField>
    </Column>
  );
};

export default RevaluationOptions;
